/**
 * Data for the modern dashboards — all real, all from existing endpoints.
 *
 *  - useSeriesPair:  /v1/dashboard/series for this period AND the one before,
 *                    so a tile can say "+18% vs previous period" honestly.
 *  - useDiaryWindow: /v1/appointments/diary over this period + the previous
 *                    one → daily appointment / test-drive counts and deltas
 *                    (the "bookings" series counts Booked-stage leads, not
 *                    appointments, so it can't be used for this).
 *  - useTodaySchedule: today's diary for one consultant.
 */
import { useEffect, useMemo, useState } from 'react';
import { appointmentsService, dashboardService, leadsService } from '../../../lib/services';
import { isClosedStage } from '../../../lib/crm';
import {
  civilToISODate, formatInOrgTz, orgAddDays, orgCivilOf, orgCivilToday, orgRange,
} from '../../../lib/datetime';
import { deltaPct } from './format';

const SERIES_MAX_DAYS = 90;   // DashboardController caps a series at 90 days

const sum = (arr) => arr.reduce((a, b) => a + (Number(b) || 0), 0);

/** Short x-axis label ("17 Sep") for each of the last `n` org-local days. */
export function dayLabels(n) {
  const today = orgCivilToday() || new Date();
  return Array.from({ length: n }, (_, i) => {
    const d = orgAddDays(today, i - (n - 1));
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  });
}

/**
 * A daily series for the period plus the previous period.
 * @returns {{ values:number[], total:number, prevTotal:number|null, delta:number|null, loading:boolean }}
 */
export function useSeriesPair(metric, days, chartDays = days) {
  const span = Math.max(days * 2, chartDays);
  const fetchDays = Math.min(SERIES_MAX_DAYS, span);
  const [raw, setRaw] = useState(null);
  useEffect(() => {
    let alive = true;
    dashboardService.series({ metric, days: fetchDays })
      .then((arr) => { if (alive) setRaw(Array.isArray(arr) ? arr.map((p) => Number(p.value) || 0) : []); })
      .catch(() => { if (alive) setRaw([]); });
    return () => { alive = false; };
  }, [metric, fetchDays]);

  return useMemo(() => {
    if (!raw) return { values: [], total: null, prevTotal: null, delta: null, loading: true };
    const cur = raw.slice(-days);
    const prev = raw.length >= days * 2 ? raw.slice(-days * 2, -days) : null;
    const total = sum(cur);
    const prevTotal = prev ? sum(prev) : null;
    return {
      values: raw.slice(-chartDays),
      total,
      prevTotal,
      delta: prevTotal == null ? null : deltaPct(total, prevTotal),
      loading: false,
    };
  }, [raw, days, chartDays]);
}

const LIVE = (a) => a && a.status !== 'CANCELLED';

/**
 * Appointments over this period and the previous one (org-local days).
 * @returns daily appointment / test-drive counts for the chart window, totals,
 *          deltas, AI-booked count and the raw rows.
 */
export function useDiaryWindow({ days, chartDays = days, consultantId = null }) {
  const span = Math.max(days * 2, chartDays);
  const [rows, setRows] = useState(null);
  const today = orgCivilToday();
  const todayKey = today ? civilToISODate(today) : '';

  useEffect(() => {
    if (!todayKey) return undefined;
    let alive = true;
    const t = orgCivilToday();
    const { fromISO, toISO } = orgRange(orgAddDays(t, -(span - 1)), t);
    const params = { from: fromISO, to: toISO };
    if (consultantId) params.consultantId = consultantId;
    appointmentsService.diary(params)
      .then((r) => { if (alive) setRows(Array.isArray(r) ? r : (r?.content || [])); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [span, consultantId, todayKey]);

  return useMemo(() => {
    if (!rows || !today) {
      return { loading: true, appts: [], drives: [], apptTotal: null, driveTotal: null, apptDelta: null, driveDelta: null, aiBooked: null, rows: [] };
    }
    const keyOf = (iso) => { const c = orgCivilOf(iso); return c ? civilToISODate(c) : null; };
    const dayKeys = (n, offset = 0) => Array.from({ length: n }, (_, i) => civilToISODate(orgAddDays(today, i - (n - 1) - offset)));
    const count = (keys, pred) => {
      const set = new Set(keys);
      const per = Object.fromEntries(keys.map((k) => [k, 0]));
      rows.forEach((a) => { if (!pred(a)) return; const k = keyOf(a.startsAt); if (set.has(k)) per[k] += 1; });
      return keys.map((k) => per[k]);
    };
    const isDrive = (a) => LIVE(a) && a.type === 'TEST_DRIVE';
    const curKeys = dayKeys(days);
    const prevKeys = dayKeys(days, days);
    const chartKeys = dayKeys(chartDays);
    const apptTotal = sum(count(curKeys, LIVE));
    const driveTotal = sum(count(curKeys, isDrive));
    const apptPrev = sum(count(prevKeys, LIVE));
    const drivePrev = sum(count(prevKeys, isDrive));
    const curSet = new Set(curKeys);
    const aiBooked = rows.filter((a) => LIVE(a) && a.source === 'AI' && curSet.has(keyOf(a.createdAt || a.startsAt))).length;
    return {
      loading: false,
      appts: count(chartKeys, LIVE),
      drives: count(chartKeys, isDrive),
      apptTotal, driveTotal,
      apptDelta: deltaPct(apptTotal, apptPrev),
      driveDelta: deltaPct(driveTotal, drivePrev),
      aiBooked,
      rows,
    };
  }, [rows, today, days, chartDays]);
}

/** Today's appointments for one consultant (or everyone), soonest first. */
export function useTodaySchedule(consultantId) {
  const [rows, setRows] = useState(null);
  const today = orgCivilToday();
  const todayKey = today ? civilToISODate(today) : '';
  useEffect(() => {
    if (!todayKey) return undefined;
    let alive = true;
    const t = orgCivilToday();
    const { fromISO, toISO } = orgRange(t, t);
    const params = { from: fromISO, to: toISO };
    if (consultantId) params.consultantId = consultantId;
    appointmentsService.diary(params)
      .then((r) => {
        if (!alive) return;
        const list = (Array.isArray(r) ? r : (r?.content || [])).filter(LIVE);
        list.sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt));
        setRows(list);
      })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [consultantId, todayKey]);
  return rows;
}

/** "9:00 am" in the dealership's zone. */
export const timeOf = (iso) => formatInOrgTz(iso, { hour: 'numeric', minute: '2-digit' }, '');

/** Lead created / moved inside the last `days` org-local days. */
export function withinDays(iso, days) {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) && t >= Date.now() - days * 86_400_000;
}

/** "Good morning / afternoon / evening" by the dealership's clock. */
export function greeting() {
  const h = Number(formatInOrgTz(new Date(), { hour: 'numeric', hour12: false }, '12'));
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

/* ---------------------------------------------------------------- pipeline */
// The Lead Accelerator's stages, in board order, with the board's own labels.
const PIPELINE = [
  { key: 'INBOUND',         label: 'Inbound',     c: ['#818cf8', '#6366f1'] },
  { key: 'CONTACTED',       label: 'Contacted',   c: ['#60a5fa', '#3b82f6'] },
  { key: 'APPOINTMENT',     label: 'Qualified',   c: ['#a78bfa', '#8b5cf6'] },
  { key: 'TEST_DRIVE',      label: 'Test drive',  c: ['#c084fc', '#a855f7'] },
  { key: 'NEGOTIATION',     label: 'Negotiation', c: ['#fbbf24', '#f59e0b'] },
  { key: 'CONFIRMED',       label: 'Quoted',      c: ['#fdba74', '#f97316'] },
  { key: 'DELIVERY_BOOKED', label: 'Booked',      c: ['#6ee7b7', '#10b981'] },
  { key: 'RETAILED',        label: 'Retailed',    c: ['#5eead4', '#14b8a6'] },
  { key: 'DELIVERED',       label: 'Delivered',   c: ['#86efac', '#16a34a'] },
];
const STAGE_INDEX = Object.fromEntries(PIPELINE.map((p, i) => [p.key, i]));
/**
 * The current pipeline as a funnel over every board stage.
 *  reached — leads at this stage or beyond (Inbound = every lead, lost too)
 *  now     — leads sitting in this stage right now
 * Lost leads count once, at intake, and are reported separately.
 * @returns {{ stages: Array<{key,label,colors,reached,now}>, lost:number, total:number }}
 */
export function pipelineFunnel(leads = []) {
  const idx = leads.map((l) => STAGE_INDEX[l.stage]);
  const total = leads.length;
  const stages = PIPELINE.map((p, i) => ({
    key: p.key,
    label: p.label,
    colors: p.c,
    reached: i === 0 ? total : idx.filter((x) => x != null && x >= i).length,
    now: leads.filter((l) => l.stage === p.key).length,
  }));
  return { stages, lost: leads.filter((l) => l.stage === 'LOST').length, total };
}

/* ---------------------------------------------------------------- car renders */
const slug = (s) => String(s || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
/**
 * A clean studio render for a car. Uses the stock photo when it is already a
 * studio render; otherwise builds one from make + model, because dealer stock
 * photos are often placeholders (a grey box with the model name on it).
 */
export function carRender(make, model, photoUrl) {
  if (photoUrl && /cdn\.imagin\.studio/i.test(photoUrl)) return photoUrl;
  const mk = slug(make);
  const family = slug(String(model || '').split(/\s+/)[0]);
  if (!mk || !family) return null;
  return `https://cdn.imagin.studio/getimage?customer=img&make=${mk}&modelFamily=${family}&angle=23&width=800`;
}

/* ---------------------------------------------------------------- showroom */
const audFull = (cents) => `$${Math.round(cents / 100).toLocaleString('en-AU')}`;
/**
 * Carousel slides for the hero: one per ranked model (studio render, live
 * lead count, from-price and in-stock count from real stock). Falls back to
 * the priciest in-stock models when nothing is ranked yet.
 * models: [{ key, label, value, photo }] (from a RankList source).
 */
export function showroomSlides(models = [], vehicles = [], { badge, unit = 'lead' } = {}) {
  const stockFor = (key) => {
    const first = String(key).toUpperCase().split(' ')[0];
    return vehicles.filter((v) => v.stockStatus === 'IN_STOCK' && String(v.model || '').toUpperCase().startsWith(first));
  };
  const ranked = models.filter((m) => m.photo).slice(0, 5).map((m, i) => {
    const stock = stockFor(m.key);
    const prices = stock.map((v) => v.driveAwayPriceCents).filter((c) => c > 0);
    const facts = [`${m.value} ${unit}${m.value === 1 ? '' : 's'}`];
    if (prices.length) facts.push(`From ${audFull(Math.min(...prices))}`);
    facts.push(stock.length ? `${stock.length} in stock` : 'Not in stock');
    return { key: m.key, photo: m.photo, title: m.label, badge: badge ? badge(i) : null, facts, to: '/inventory' };
  });
  if (ranked.length) return ranked;

  const seen = new Set();
  return vehicles
    .filter((v) => v.stockStatus === 'IN_STOCK')
    .sort((a, b) => (b.driveAwayPriceCents || 0) - (a.driveAwayPriceCents || 0))
    .filter((v) => { const k = `${v.make} ${v.model}`.toUpperCase(); if (seen.has(k)) return false; seen.add(k); return true; })
    .slice(0, 5)
    .map((v) => ({
      key: v.id,
      photo: carRender(v.make, v.model, v.primaryPhotoUrl),
      title: `${v.make} ${v.model}`,
      badge: 'In stock now',
      facts: [v.driveAwayPriceCents ? audFull(v.driveAwayPriceCents) : null, v.condition ? v.condition.toLowerCase() : null].filter(Boolean),
      to: '/inventory',
    }))
    .filter((s) => s.photo);
}

/* ---------------------------------------------------------------- exceptions */
/**
 * Leads that need a decision — the same leads /leads?filter=exceptions lists
 * (idle 24h+, unassigned, or no next step), so the banner and the board agree.
 * Counts each lead once; the snapshot's exception block counts open alerts,
 * and one lead can carry several. null until the next events are in.
 */
export function useExceptionCount(leads = []) {
  // { map, at } — map is null when the endpoint failed; at = fetch time.
  const [res, setRes] = useState(null);
  const openIds = useMemo(
    () => leads.filter((l) => !isClosedStage(l.stage)).map((l) => l.id).filter(Boolean),
    [leads],
  );
  useEffect(() => {
    let cancelled = false;
    if (!openIds.length) return undefined;
    leadsService.nextEvents(openIds)
      .then((map) => { if (!cancelled) setRes({ map: map || {}, at: Date.now() }); })
      .catch(() => { if (!cancelled) setRes({ map: null, at: Date.now() }); });
    return () => { cancelled = true; };
  }, [openIds]);

  return useMemo(() => {
    if (!openIds.length) return leads.length ? 0 : null;
    if (!res) return null;
    const dayAgo = res.at - 24 * 60 * 60 * 1000;
    return leads.filter((l) => {
      if (isClosedStage(l.stage)) return false;
      const last = l.lastActivityAt || l.createdAt;
      const idle = !!last && new Date(last).getTime() < dayAgo;
      // Like the board: "no next step" only counts when next events loaded.
      const noNext = res.map != null && !res.map[l.id];
      return idle || !l.assignedConsultantId || noNext;
    }).length;
  }, [leads, openIds, res]);
}

/* ---------------------------------------------------------------- module snapshot */
/**
 * /v1/dashboard/snapshot — the module counts the role snapshot doesn't carry
 * (revival targets, voice QA). null while loading or if the call fails.
 */
export function useDashboardSnapshot(days) {
  const [snap, setSnap] = useState(null);
  useEffect(() => {
    let alive = true;
    dashboardService.snapshot({ days })
      .then((s) => { if (alive) setSnap(s || null); })
      .catch(() => { if (alive) setSnap(null); });
    return () => { alive = false; };
  }, [days]);
  return snap;
}
